import React from "react";
import { AlertTriangle, type LucideIcon } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ReportsMetaIconChip } from "../Reports/components/ReportsCardChrome";
import { cn } from "@/lib/utils";
import type {
  StrategyRdApplyOverview,
  StrategyRdBacktestWarning,
  StrategyRdMarginSummary,
  StrategyRdParity,
} from "../../../types/strategyRd";
import { statsCardHeadlineClassName } from "../Statistics/components/statisticsCardHeadline";
import {
  StrategyRdParityBadge,
  StrategyRdSideToggle,
  strategyRdSideOverview,
  type StrategyRdSideView,
} from "./StrategyRdApplyTable";
import { StrategyRdBacktestWarnings } from "./StrategyRdBacktestWarnings";
import { StrategyRdStabilitySection } from "./StrategyRdStability";
import { StrategyRdRobustnessReport } from "./StrategyRdRobustnessReport";
import { StrategyRdEquityHero } from "./StrategyRdEquityHero";
import { StrategyRdCostsCompare } from "./StrategyRdCostsCompare";
import {
  StrategyRdOverviewHeroes,
  StrategyRdOverviewStats,
  type StrategyRdReturnRange,
} from "./StrategyRdOverviewStats";
import { StrategyRdSweepTableView } from "./coach/StrategyRdCoachLabActionCard";
import { strategyRdMetaClassName } from "./strategyRdCardShell";
import { strategyRdCardTitleClassName, strategyRdSectionCardClassName } from "./strategyRdLabUi";

/** One fact about the run, shown as a workspace-style metadata icon chip (hover for label + value). */
export type StrategyRdResultsMeta = { id: string; label: string; value: string; icon: LucideIcon };

export type StrategyRdLabResultsTab = "overview" | "trades" | "robustness" | "account" | "versions";

const TABS: { value: StrategyRdLabResultsTab; label: string }[] = [
  { value: "overview", label: "Overview" },
  { value: "trades", label: "Trades" },
  { value: "robustness", label: "Robustness" },
  { value: "account", label: "Account test" },
  { value: "versions", label: "Versions" },
];

/**
 * Pinned above the results tabs: what was tested (name, period, setup counts), the headline
 * numbers for the chosen side, what to watch for (parity, warnings) and what to do next.
 */
export function StrategyRdLabResultsHeader({
  title,
  meta = [],
  overview,
  side,
  onSideChange,
  tab,
  onTabChange,
  currency,
  parity,
  warnings,
  onCheckRobustness,
  onOpenSetting,
  actions,
  actionsHint,
  onUseCosts,
  marginCallTimes,
}: {
  /** Times of margin calls in this backtest's trades (markers on the account chart). */
  marginCallTimes?: string[] | null;
  title: string;
  /** Period, setups, data, window: icon chips beside the name, like workspace canvas nodes. */
  meta?: StrategyRdResultsMeta[];
  overview: StrategyRdApplyOverview;
  side: StrategyRdSideView;
  onSideChange: (side: StrategyRdSideView) => void;
  tab: StrategyRdLabResultsTab;
  onTabChange: (tab: StrategyRdLabResultsTab) => void;
  currency: string | null;
  parity: StrategyRdParity | null;
  warnings: StrategyRdBacktestWarning[] | null;
  onCheckRobustness: () => void;
  onOpenSetting: (field: string) => void;
  actions: React.ReactNode;
  /** Why Save as strategy is unavailable, under the actions. */
  actionsHint?: string | null;
  /** Adopt the typical costs shown next to a cost-free backtest, and run it again. */
  onUseCosts?: (costs: Partial<import("../../../types/strategyRd").StrategyRdSizing>) => void;
}): JSX.Element {
  const sideOverview = strategyRdSideOverview(overview, side);
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex min-w-0 flex-col gap-4" data-strategy-rd-results-header="">
        {/* What was tested and what to do with it: its own card, actions on one line under the name. */}
        <div className={cn(strategyRdSectionCardClassName, "gap-4")} data-strategy-rd-results-identity="">
          <div className="flex min-w-0 flex-col gap-1">
            <span className={statsCardHeadlineClassName}>Backtest</span>
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
              <h3 className="m-0 min-w-0 truncate font-[family-name:var(--font-primary)] text-lg font-medium leading-tight text-[color:var(--brand-tropical-surf)]">
                {title}
              </h3>
              {meta.length > 0 ? (
                <div className="flex shrink-0 items-center gap-1" data-strategy-rd-results-meta="">
                  {meta.map((m) => (
                    <ReportsMetaIconChip key={m.id} id={m.id} label={m.label} value={m.value} icon={m.icon} />
                  ))}
                </div>
              ) : null}
            </div>
          </div>
          <div className="flex flex-col gap-1.5 !border-0 !border-t !border-solid !border-border pt-4">
            <div className="flex flex-wrap items-center gap-2">{actions}</div>
            {actionsHint ? (
              <p className={cn(strategyRdMetaClassName, "text-xs")} data-strategy-rd-save-hint="">
                {actionsHint}
              </p>
            ) : null}
          </div>
        </div>

        {/* Tabs first, as on Statistics: each tab is its own page of the results. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Tabs className="min-w-0 max-w-full" data-trade-log-tabs="" value={tab} onValueChange={(v) => onTabChange(v as StrategyRdLabResultsTab)}>
            {/* Narrow: the tabs scroll sideways (no scrollbar) instead of being cut off. */}
            <div className="max-w-full overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <TabsList className="min-w-max" data-strategy-rd-results-tabs="">
                {TABS.map((t) => (
                  <TabsTrigger key={t.value} value={t.value}>
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
          </Tabs>
          <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2">
            <StrategyRdParityBadge parity={parity} />
          </div>
        </div>
        <StrategyRdBacktestWarnings
          compact
          warnings={
            overview.with_realistic_costs
              ? (warnings ?? []).filter((w) => w.code !== "costs_missing" && w.code !== "financing_not_included")
              : warnings
          }
          onCheckRobustness={onCheckRobustness}
          onOpenSetting={onOpenSetting}
        />

        {tab === "overview" ? (
          <>
            <StrategyRdOverviewHeroes overview={sideOverview} currency={currency} />
            <StrategyRdEquityHero
              points={sideOverview?.equity_curve}
              startingEquity={sideOverview?.starting_equity ?? null}
              currency={currency}
              marginCallTimes={marginCallTimes}
              headerAction={<StrategyRdSideToggle overview={overview} value={side} onChange={onSideChange} />}
            />
            <StrategyRdCostsCompare
              withoutCosts={overview}
              withCosts={overview.with_realistic_costs}
              onUseCosts={onUseCosts}
            />
          </>
        ) : null}
      </div>
    </TooltipProvider>
  );
}

/**
 * Overview tab: Outcomes and Economics, equity and P&L charts, the hold-out split and — when
 * TES ran a parameter search — the search table.
 */
export function StrategyRdLabOverview({
  overview,
  fullOverview,
  side,
  fills,
  currency,
  margin,
  warnings,
  returnRange,
  onAskTes,
}: {
  /** The chosen side's overview. */
  overview: StrategyRdApplyOverview | null;
  fullOverview: StrategyRdApplyOverview;
  side: StrategyRdSideView;
  fills: React.ComponentProps<typeof StrategyRdOverviewStats>["fills"];
  currency: string | null;
  margin: StrategyRdMarginSummary | null;
  warnings: StrategyRdBacktestWarning[] | null;
  returnRange: StrategyRdReturnRange | null;
  /** Opens TES with a question about these results. */
  onAskTes?: (question: string) => void;
}): JSX.Element {
  // The side's own split when it has one, else all trades' (the other tiles always read all
  // trades) — Longs / Shorts used to drop the unseen-period tile.
  const split = overview?.split ?? fullOverview.split;
  const stability = fullOverview.stability;
  return (
    <div className="flex flex-col gap-4" data-strategy-rd-results-overview="">
      {/* Older backtests without `warnings[]`: the margin-call banner of §2. */}
      {warnings == null && margin?.margin_calls != null && margin.margin_calls > 0 ? (
        <Alert data-strategy-rd-margin-banner="">
          <AlertTriangle className="size-4 text-muted-foreground" aria-hidden />
          <AlertDescription className={strategyRdMetaClassName}>
            TradingView would have margin-called {margin.margin_calls} trade
            {margin.margin_calls === 1 ? "" : "s"}. Lower the risk % or raise leverage.
          </AlertDescription>
        </Alert>
      ) : null}
      {/* Top down: is the edge real → how bad can it get → every number behind the headline. */}
      <StrategyRdStabilitySection split={split} stability={stability} />
      <StrategyRdRobustnessReport report={fullOverview.report} onAskTes={onAskTes} />
      <StrategyRdOverviewStats
        overview={overview}
        showHeroes={false}
        fills={fills}
        currency={currency}
        margin={side === "combined" ? margin : null}
        returnRange={side === "combined" ? returnRange : null}
      />
      {fullOverview.sweep ? (
        <div className={strategyRdSectionCardClassName}>
          <div className="flex flex-col gap-0.5">
            <h3 className={strategyRdCardTitleClassName}>Parameter search</h3>
            <p className={cn(strategyRdMetaClassName, "text-xs")}>
              The best row (on the tuning period) is this backtest.
            </p>
          </div>
          <StrategyRdSweepTableView sweep={fullOverview.sweep} />
        </div>
      ) : null}
    </div>
  );
}
